// FlavorCard.tsx
import React from 'react';
import {
  Star,
  CheckCircle,
  Apple,
  Citrus,
  Cookie,
  Coffee,
  Flame,
  Leaf,
  CircleDot,
  HelpCircle,
} from 'lucide-react';
import { FlavorDetails } from '../repositories/LocalDatabase';

const SERIES_ICONS: Record<string, React.ReactNode> = {
  's-fruit': <Apple size={14} />,
  's-fruit-sour': <Citrus size={14} />,
  's-dessert': <Cookie size={14} />,
  's-cafe': <Coffee size={14} />,
  's-spice': <Flame size={14} />,
  's-mint': <Leaf size={14} />,
  's-nut': <CircleDot size={14} />,
  's-other': <HelpCircle size={14} />,
};

interface FlavorCardProps {
  flavor: FlavorDetails;
  onClick: () => void;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
}

export default function FlavorCard({ flavor, onClick, selectable, selected, onSelect }: FlavorCardProps) {
  return (
    <div
      className="ripple-card"
      onClick={onClick}
      style={{
        backgroundColor: 'var(--color-surface)',
        borderRadius: '12px',
        padding: '16px',
        border: '1px solid rgba(231, 228, 223, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s ease-out',
        position: 'relative',
        minHeight: '130px',
        overflow: 'hidden',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.borderColor = 'var(--color-primary)';
        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.borderColor = 'rgba(231, 228, 223, 0.08)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {selectable && onSelect && (
            <input
              type="checkbox"
              checked={selected || false}
              onChange={(e) => {
                e.stopPropagation(); // prevent card click
                onSelect(e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()} // prevent card click
              style={{
                width: '18px',
                height: '18px',
                marginRight: '8px',
                cursor: 'pointer',
                accentColor: 'var(--color-primary)',
              }}
            />
          )}
          {flavor.series_id && SERIES_ICONS[flavor.series_id] && (
            <span
              style={{
                color: 'var(--color-accent)',
                opacity: 0.8,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {SERIES_ICONS[flavor.series_id]}
            </span>
          )}
          <span
            style={{
              fontSize: 'var(--font-size-caption)',
              color: 'var(--color-accent)',
              fontFamily: 'var(--font-heading-en)',
              letterSpacing: '0.5px',
            }}
          >
            {flavor.brand_name}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {!flavor.nicotine && (
            <span
              style={{
                fontSize: '11px',
                color: 'var(--color-accent)',
                backgroundColor: 'rgba(201, 166, 107, 0.15)',
                border: '1px solid rgba(201, 166, 107, 0.3)',
                padding: '1px 6px',
                borderRadius: '4px',
                fontWeight: '500',
              }}
            >
              ノンニコチン
            </span>
          )}
          {flavor.owned ? (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                color: 'var(--color-success)',
                backgroundColor: 'rgba(91, 161, 139, 0.1)',
                padding: '2px 8px',
                borderRadius: '20px',
              }}
            >
              <CheckCircle size={10} />
              所持
            </span>
          ) : (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                color: 'rgba(231, 228, 223, 0.4)',
                backgroundColor: 'rgba(231, 228, 223, 0.04)',
                padding: '2px 8px',
                borderRadius: '20px',
              }}
            >
              未所持
            </span>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <h4
          style={{
            fontSize: 'var(--font-size-body)',
            fontWeight: '600',
            color: 'var(--color-text)',
            margin: 0,
          }}
        >
          {flavor.name}
        </h4>
        {flavor.series_name && (
          <span
            style={{
              fontSize: '12px',
              opacity: 0.6,
              marginTop: '4px',
            }}
          >
            系統: {flavor.series_name}
          </span>
        )}
      </div>

      {flavor.description && (
        <p
          style={{
            fontSize: 'var(--font-size-caption)',
            opacity: 0.7,
            margin: 0,
            lineHeight: '1.4',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {flavor.description}
        </p>
      )}

      {flavor.owned && flavor.rating && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            marginTop: 'auto',
          }}
        >
          {Array.from({ length: 5 }).map((_, idx) => (
            <Star
              key={idx}
              size={12}
              fill={
                idx < flavor.rating! ? 'var(--color-accent)' : 'transparent'
              }
              color={
                idx < flavor.rating!
                  ? 'var(--color-accent)'
                  : 'rgba(231, 228, 223, 0.2)'
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
